import React from 'react';
import { ActivityIndicator, Modal as ReactModal, TouchableOpacity, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useVideoPlayer, VideoView } from 'expo-video';
import { Ionicons } from '@expo/vector-icons';

import api from '../../../../utils/api';
import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';

const PROBLEMS = {
  not_on_agent: ['Sin configurar', 'Esta cámara todavía no está configurada para verse en vivo'],
  no_tunnel: ['Sin configurar', 'Esta cámara todavía no está configurada para verse en vivo'],
  site_offline: ['Caseta sin conexión', 'El equipo de la caseta está sin conexión a internet'],
  camera_offline: ['Sin señal', 'La cámara no está enviando video'],
};

/** Por qué no se puede ver una cámara (`status`/`reason` de la API), corto o completo. */
export function cameraProblem(reason: string, short = false) {
  const [title, detail] = PROBLEMS[reason] ?? ['Sin señal', 'No se pudo abrir el video de esta cámara'];
  return short ? title : detail;
}

// Reproductor aislado: sólo se monta cuando ya hay url, así useVideoPlayer
// siempre recibe una fuente real (no podemos llamar el hook condicionalmente).
function LivePlayer({ url }: { url: string }) {
  const player = useVideoPlayer(url, player => {
    player.play();
  });

  return (
    <VideoView
      style={tw`flex-1`}
      player={player}
      contentFit="contain"
      allowsFullscreen
      nativeControls
    />
  );
}

export default function CameraPlayerModal({ communityId, camera, onRequestClose }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['community.camera.live', communityId, camera.uuid],
    queryFn: () =>
      api
        .get(`/communities/${communityId}/sentinel/cameras/${camera.uuid}/live`)
        .then(response => response.data),
    // El token de la URL es de vida corta; no la dejamos cacheada vieja.
    staleTime: 0,
    gcTime: 0,
  });

  return (
    <ReactModal visible transparent animationType="fade" onRequestClose={onRequestClose}>
      <View style={tw`flex-1 bg-black`}>
        {/* Header */}
        <View style={tw`row justify-between items-center px-4 pt-12 pb-3`}>
          <View style={tw`row items-center`}>
            <View style={tw`h-2.5 w-2.5 rounded-full bg-red mr-2`} />
            <Text style={tw`text-white text-base`}>{camera.name}</Text>
          </View>
          <TouchableOpacity onPress={onRequestClose} style={tw`p-2`}>
            <Ionicons name="close" size={26} color="white" />
          </TouchableOpacity>
        </View>

        {/* Cuerpo */}
        <View style={tw`flex-1`}>
          {isLoading && (
            <View style={tw`flex-1 items-center justify-center`}>
              <ActivityIndicator color="white" />
              <Text style={tw`text-white/70 mt-3`}>Conectando…</Text>
            </View>
          )}

          {(isError || data?.available === false) && (
            <View style={tw`flex-1 items-center justify-center px-8`}>
              <Ionicons name="videocam-off-outline" size={48} color={tw.color('gray-500')} />
              <Text style={tw`text-white/70 mt-3 text-center`}>
                {cameraProblem(isError ? 'error' : data.reason)}
              </Text>
            </View>
          )}

          {!isLoading && !isError && data?.available && <LivePlayer url={data.url} />}
        </View>
      </View>
    </ReactModal>
  );
}
